import type { Phase, TraBase } from '@ics-portal/tra';

export interface PhaseActivationState {
    error: string[] | null;
    phase: Phase;
    nextPhase: Phase;
}

export class TraPhaseHelper<T extends TraBase> {
    constructor(private readonly data: T) {}

    hasPhaseToLock(
        nextPhase: Phase,
        currentPhase: Phase,
    ): PhaseActivationState | undefined {
        const phaseList = this.data.phases.getAll().filter((phase) => {
            const phaseIndex = parseInt(phase.id, 10);
            return (
                phaseIndex < parseInt(nextPhase.id) &&
                phaseIndex >= parseInt(currentPhase.id)
            );
        });

        const phaseToLock = phaseList.find(
            (phase) => this.data.phaseLockRules[phase.name] && !phase.lockedAt,
        );
        if (phaseToLock) {
            const rule = this.data.phaseLockRules[phaseToLock.name];
            return {
                error: rule ? [rule.message] : null,
                phase: phaseToLock,
                nextPhase,
            };
        }
        return undefined;
    }

    hasPhaseToComplete({
        nextPhase,
        selectedPhase,
    }: {
        nextPhase: Phase;
        selectedPhase: Phase;
    }): PhaseActivationState {
        const result = { phase: nextPhase, nextPhase, error: null };
        const currentPhase = this.data.phases.getCurrentPhase();
        const currentPhaseErrors =
            this.data.phaseValidator.getCompleteErrors(currentPhase);
        const nextPhaseErrors =
            this.data.phaseValidator.getCompleteErrors(nextPhase);

        if (!selectedPhase.isAfter(nextPhase)) {
            return result;
        }

        if (currentPhase && currentPhaseErrors) {
            return {
                ...result,
                error: currentPhaseErrors,
            };
        }

        if (selectedPhase.isAfter(nextPhase)) {
            return {
                ...result,
                error: nextPhaseErrors,
            };
        }
        return result;
    }

    getNextPossiblePhase(phase: Phase): Phase {
        const completedPhases = this.completedPhases();

        if (completedPhases.some((p) => p.id === phase.id)) {
            return phase;
        }
        return (
            this.data.phases.getById(
                completedPhases[completedPhases.length - 1].nextPhaseId,
            ) ?? phase
        );
    }

    childPhase(phase: Phase): Phase {
        if (phase.rootPhaseId) {
            return phase;
        }
        return this.data.phases.getChildPhases(phase)[0];
    }

    private completedPhases(): Phase[] {
        const phases = this.data.phases
            .getAll()
            .filter((phase) => phase.rootPhaseId);
        const completedPhases: Phase[] = [];

        for (const phase of phases) {
            if (this.data.phaseValidator.getCompleteErrors(phase)) {
                break;
            }
            completedPhases.push(phase);
        }

        return completedPhases;
    }
}
